import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { Location } from "@/data/locations";

interface LocationCardProps {
  location: Location;
  /** /book shows the longer blurb; the landing page grid does not. */
  showDescription?: boolean;
}

/**
 * One card treatment shared by the landing page grid and /book, so the two cannot
 * drift into different designs for the same data.
 */
const LocationCard = ({ location, showDescription = false }: LocationCardProps) => {
  return (
    <Link
      to={location.link}
      className="group flex flex-col h-full rounded-2xl overflow-hidden border border-quietcove-sand/70 bg-white transition-colors duration-500 hover:border-gold/60"
    >
      <div className="aspect-[16/10] overflow-hidden bg-secondary">
        <img
          src={location.image}
          alt={location.title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
        />
      </div>

      <div className="flex flex-col grow p-7">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-6 bg-gold" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-deep">
            {location.city}
          </span>
        </div>

        <h3 className="font-display text-xl font-bold tracking-[-0.015em] text-quietcove-blue mb-3 leading-snug">
          {location.title}
        </h3>

        {showDescription && (
          <p className="text-quietcove-grey leading-relaxed mb-6 grow">{location.description}</p>
        )}

        <span
          className={`inline-flex items-center gap-2 text-quietcove-blue font-semibold text-sm group-hover:text-gold-deep transition-colors duration-300 ${
            showDescription ? "" : "mt-auto"
          }`}
        >
          Book a pod
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
        </span>
      </div>
    </Link>
  );
};

export default LocationCard;
